/**
 * Shared layout primitives for the admin dashboard.
 *
 * These are not a new design system - they are the exact inline-style recipes
 * already used by ExpensesPage/ServicePricingPage, extracted once so screens
 * cannot drift apart. Colours always come from useColors() so dark mode keeps
 * working.
 *
 * PROVENANCE: an identical copy currently lives at
 * features/b2b/components/primitives.tsx. This is the canonical one - it is
 * shipped, and it is what the Notification Center uses. The B2B copy is left in
 * place only because that feature is still in flight on another branch; when it
 * lands it should import from here and its copy should be deleted.
 */
import type {
  ChangeEvent,
  CSSProperties,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { useColors } from '../../hooks/useColors'
import { useModalA11y } from '../../hooks/useModalA11y'
import {
  CARD_RADIUS,
  CARD_SHADOW,
  INPUT_RADIUS,
  PILL_RADIUS,
  SECTION_RADIUS,
} from './uiTokens'

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string
  subtitle?: string
  actions?: ReactNode
}) {
  const colors = useColors()

  return (
    <header
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '22px',
        flexWrap: 'wrap',
      }}
    >
      <div>
        <h1 className="font-display" style={{ margin: 0, fontSize: '26px', color: colors.textPrimary }}>
          {title}
        </h1>
        {subtitle ? (
          <p className="font-body" style={{ margin: '6px 0 0', color: colors.textMuted }}>
            {subtitle}
          </p>
        ) : null}
      </div>
      {actions ? <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>{actions}</div> : null}
    </header>
  )
}

export function Card({
  children,
  padded = true,
  section = false,
  style,
}: {
  children: ReactNode
  padded?: boolean
  section?: boolean
  style?: CSSProperties
}) {
  const colors = useColors()

  return (
    <div
      style={{
        background: colors.bgCard,
        borderRadius: section ? SECTION_RADIUS : CARD_RADIUS,
        padding: padded ? '20px' : 0,
        border: `1px solid ${colors.border}`,
        boxShadow: CARD_SHADOW,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

export function CardTitle({ children, right }: { children: ReactNode; right?: ReactNode }) {
  const colors = useColors()

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '14px',
      }}
    >
      <h3 className="font-body" style={{ margin: 0, fontSize: '15px', color: colors.textPrimary }}>
        {children}
      </h3>
      {right}
    </div>
  )
}

export function StatCard({
  label,
  value,
  color,
  hint,
}: {
  label: string
  value: string
  color?: string
  hint?: string
}) {
  const colors = useColors()

  return (
    <div
      style={{
        flex: 1,
        minWidth: '170px',
        background: colors.bgCard,
        borderRadius: CARD_RADIUS,
        padding: '18px',
        boxShadow: CARD_SHADOW,
        border: `1px solid ${colors.border}`,
      }}
    >
      <div
        style={{
          fontSize: '11px',
          fontWeight: 700,
          color: colors.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: '26px', fontWeight: 700, color: color ?? colors.textPrimary, marginTop: '8px' }}>
        {value}
      </div>
      {hint ? (
        <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '4px' }}>{hint}</div>
      ) : null}
    </div>
  )
}

export function StatRow({ children }: { children: ReactNode }) {
  return (
    <section style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '22px' }}>
      {children}
    </section>
  )
}

type ButtonProps = {
  children: ReactNode
  onClick?: () => void
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost'
  disabled?: boolean
  fullWidth?: boolean
  type?: 'button' | 'submit'
  title?: string
  style?: CSSProperties
}

export function PillButton({
  children,
  onClick,
  variant = 'primary',
  disabled = false,
  fullWidth = false,
  type = 'button',
  title,
  style,
}: ButtonProps) {
  const colors = useColors()

  const palette: Record<string, CSSProperties> = {
    primary: { background: colors.textPrimary, color: colors.surface, border: 'none' },
    secondary: {
      background: colors.bgCard,
      color: colors.textPrimary,
      border: `1px solid ${colors.border}`,
    },
    outline: {
      background: colors.bgCard,
      color: colors.textPrimary,
      border: `1px solid ${colors.textPrimary}`,
    },
    ghost: { background: 'transparent', color: colors.textPrimary, border: 'none' },
  }

  return (
    <button
      type={type}
      title={title}
      onClick={onClick}
      disabled={disabled}
      className="font-body"
      style={{
        borderRadius: PILL_RADIUS,
        padding: '10px 18px',
        fontWeight: 600,
        fontSize: '13px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        width: fullWidth ? '100%' : undefined,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        ...palette[variant],
        ...style,
      }}
    >
      {children}
    </button>
  )
}

export function Chip({
  children,
  tone = 'neutral',
  style,
}: {
  children: ReactNode
  tone?: 'neutral' | 'success' | 'warning' | 'info' | 'gold'
  style?: CSSProperties
}) {
  const colors = useColors()

  const tones: Record<string, CSSProperties> = {
    neutral: { background: colors.surface, color: colors.textSecondary, border: `1px solid ${colors.border}` },
    success: { background: 'rgba(21,128,61,0.10)', color: '#15803D', border: '1px solid rgba(21,128,61,0.25)' },
    warning: { background: 'rgba(180,83,9,0.10)', color: '#B45309', border: '1px solid rgba(180,83,9,0.25)' },
    info: { background: 'rgba(29,78,216,0.10)', color: '#1D4ED8', border: '1px solid rgba(29,78,216,0.25)' },
    gold: { background: 'rgba(214,185,123,0.18)', color: colors.accentText, border: '1px solid rgba(214,185,123,0.45)' },
  }

  return (
    <span
      className="font-body"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        borderRadius: PILL_RADIUS,
        padding: '4px 10px',
        fontSize: '12px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        ...tones[tone],
        ...style,
      }}
    >
      {children}
    </span>
  )
}

export function Field({
  label,
  hint,
  error,
  children,
  style,
}: {
  label?: string
  hint?: string
  error?: string
  children: ReactNode
  style?: CSSProperties
}) {
  const colors = useColors()

  return (
    <div style={style}>
      {label ? (
        <label
          className="font-body"
          style={{ display: 'block', fontSize: '12px', color: colors.textMuted, marginBottom: '4px' }}
        >
          {label}
        </label>
      ) : null}
      {children}
      {error ? (
        <p className="font-body" style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--color-error)' }}>
          {error}
        </p>
      ) : hint ? (
        <p className="font-body" style={{ margin: '4px 0 0', fontSize: '12px', color: colors.textMuted }}>
          {hint}
        </p>
      ) : null}
    </div>
  )
}

const inputStyle = (colors: ReturnType<typeof useColors>, invalid?: boolean): CSSProperties => ({
  width: '100%',
  padding: '10px',
  borderRadius: INPUT_RADIUS,
  border: `1px solid ${invalid ? 'var(--color-error)' : colors.border}`,
  background: colors.bgCard,
  color: colors.textPrimary,
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '14px',
})

export function TextInput({
  invalid,
  style,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  const colors = useColors()
  return <input {...props} style={{ ...inputStyle(colors, invalid), ...style }} />
}

export function TextArea({
  invalid,
  style,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  const colors = useColors()
  return <textarea {...props} style={{ ...inputStyle(colors, invalid), resize: 'vertical', ...style }} />
}

export function SelectInput({
  style,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>) {
  const colors = useColors()
  return (
    <select {...props} style={{ ...inputStyle(colors), ...style }}>
      {children}
    </select>
  )
}

export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  size = 'md',
}: {
  value: T
  options: Array<{ value: T; label: string }>
  onChange: (value: T) => void
  size?: 'sm' | 'md'
}) {
  const colors = useColors()

  return (
    <div
      style={{
        display: 'inline-flex',
        gap: '6px',
        padding: '4px',
        background: colors.bgSecondary,
        borderRadius: PILL_RADIUS,
        border: `1px solid ${colors.border}`,
        flexWrap: 'wrap',
      }}
    >
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            className="font-body"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            style={{
              border: 'none',
              borderRadius: PILL_RADIUS,
              padding: size === 'sm' ? '5px 12px' : '8px 16px',
              fontSize: size === 'sm' ? '12px' : '13px',
              fontWeight: 600,
              cursor: 'pointer',
              background: active ? colors.textPrimary : 'transparent',
              color: active ? colors.surface : colors.textSecondary,
            }}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

export function ProgressBar({ value, max }: { value: number; max: number }) {
  const colors = useColors()
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0

  return (
    <div
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      style={{
        height: '6px',
        borderRadius: PILL_RADIUS,
        background: colors.surface,
        overflow: 'hidden',
      }}
    >
      <div style={{ width: `${pct}%`, height: '100%', background: colors.accent }} />
    </div>
  )
}

export function Modal({
  title,
  onClose,
  width = '420px',
  children,
  footer,
}: {
  title: string
  onClose: () => void
  width?: string
  children: ReactNode
  footer?: ReactNode
}) {
  const colors = useColors()
  const containerRef = useModalA11y<HTMLDivElement>(true, onClose)

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,46,42,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 60,
        padding: '24px',
      }}
      onClick={onClose}
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        style={{
          width,
          maxWidth: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          background: colors.bgCard,
          borderRadius: SECTION_RADIUS,
          padding: '22px',
          boxShadow: '0 18px 48px rgba(15,46,42,0.28)',
          color: colors.textPrimary,
        }}
      >
        <h3 className="font-body" style={{ margin: '0 0 16px', fontSize: '17px', fontWeight: 600 }}>
          {title}
        </h3>
        {children}
        {footer ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px' }}>
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export function TableHead({ columns }: { columns: Array<{ key: string; label: string; align?: 'left' | 'right' }> }) {
  const colors = useColors()

  return (
    <thead>
      <tr>
        {columns.map((column) => (
          <th
            key={column.key}
            style={{
              textAlign: column.align ?? 'left',
              padding: '12px 14px',
              fontSize: '11px',
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              borderBottom: `1px solid ${colors.border}`,
              background: colors.bgSecondary,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            {column.label}
          </th>
        ))}
      </tr>
    </thead>
  )
}

export function Td({
  children,
  align = 'left',
  strong,
  muted,
  style,
}: {
  children: ReactNode
  align?: 'left' | 'right'
  strong?: boolean
  muted?: boolean
  style?: CSSProperties
}) {
  const colors = useColors()

  return (
    <td
      style={{
        padding: '14px',
        borderBottom: `1px solid ${colors.surface}`,
        textAlign: align,
        fontWeight: strong ? 600 : 400,
        color: muted ? colors.textMuted : colors.textPrimary,
        ...style,
      }}
    >
      {children}
    </td>
  )
}

/** Debounced-free controlled search box used on every list screen. */
export function SearchInput({
  value,
  onChange,
  placeholder,
  width = '220px',
  ariaLabel,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
  width?: string
  ariaLabel?: string
}) {
  return (
    <TextInput
      type="search"
      value={value}
      aria-label={ariaLabel ?? placeholder}
      placeholder={placeholder}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
      style={{ width, padding: '8px 10px' }}
    />
  )
}
